/**
 * Hook useBagInfo — devuelve la info de bolsa WH para una orden ML.
 *
 * Endpoint: GET /api/warehouse/order/:mlOrderId/bag?seller_id=X
 * Response: BagInfo | null  (raw, NO wrapper {success,data})
 *
 * Uso típico:
 *   const { bag, isLoading, error } = useBagInfo(mlOrderId, sellerId);
 *   if (bag) return <BagBadge code={bag.bag_code} status={bag.status} />;
 *
 * Nota: el endpoint devuelve JSON crudo (no envuelto en ApiResponse),
 * así que usamos fetch directo en vez de `api.get` (cuyo tipo asume wrapper).
 */
import { useEffect, useState } from 'react';

export interface BagInfo {
  id: number;
  batch_id: number;
  batch_date: string;
  bag_code: string;
  status: 'open' | 'closed';
  closed_at: string | null;
  batch_released_at: string | null;
  /** 2026-09-10 — correo de la bolsa (depósito con dos correos) */
  courier?: { id: number; key: string; name: string; color: string; active?: boolean } | null;
}

export interface NoBagInfo {
  kind: 'no_bag';
  no_bag_reason: string;
  batch_id: number;
  batch_date: string;
  batch_released_at: string | null;
}

export type OrderBagResult = BagInfo | NoBagInfo | null;

export function useBagInfo(
  mlOrderId: number | string | null,
  sellerId: number | null
) {
  const [bag, setBag] = useState<OrderBagResult>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!mlOrderId || !sellerId) {
      setBag(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const headers: Record<string, string> = {};
    // x-agent-id es opcional pero matchea el patrón del api/client.ts
    // para warehouse endpoints — backend puede usarlo para routing.
    if (typeof localStorage !== 'undefined') {
      const agentId = localStorage.getItem('cbt_terminal_id');
      if (agentId) headers['x-agent-id'] = agentId;
    }

    fetch(`/api/warehouse/order/${mlOrderId}/bag?seller_id=${sellerId}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as OrderBagResult;
        if (cancelled) return;
        setBag(data ?? null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setBag(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mlOrderId, sellerId]);

  return { bag, isLoading, error };
}
